import { useContext, useEffect, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { Box, Card, Paragraph, TextButton } from '@zillow/constellation-10';
import {
  ListingScoreConstants,
  ScoreDetailsModal,
  ScorePieChart,
  ZRAMListingScoreConstants,
} from '@zg-rentals/rental-revenue-components';
import { LoadingText } from '@zg-rentals/loading-components';

import { syncRecommendations, useGetListingRecommendations } from '../../hooks/queries/recommendations';

import stringHelper from '../..//utils/stringHelper';
import { AppSelectors } from '../../selectors';

import { useSelector } from '../../selectors/useSelector';
import { LoadingMapContext } from '../../providers/LoadingMapContext/context';

import Heading from './components/Heading';

import type { ReactNode } from 'react';
import type { ListingScores, RecommendationType } from './interfaces';

type Props = {
  isFromPremiumAnalyticsPage?: boolean;
  isFromSOH?: boolean;
  canResumeModularListingCreation?: boolean;
  requiredFieldsErrors?: Array<boolean>;
  previewListingCTA?: ReactNode;
};

const ListingCompletenessScore = ({
  isFromPremiumAnalyticsPage = false,
  isFromSOH = false,
  canResumeModularListingCreation = false,
  requiredFieldsErrors = [],
  previewListingCTA,
}: Props) => {
  const dispatch = useDispatch();
  const {
    loadingByComponent: { ConnectedListing: isConnectedListingLoading },
  } = useContext(LoadingMapContext);

  const isScreenMobile = useSelector((state) => AppSelectors.makeIsScreenMobile(state));
  const isScreenTablet = useSelector((state) => AppSelectors.makeIsScreenTablet(state));
  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));

  const { listingAlias, isMultifamily, isFeed } = useSelector(
    (state) => ({
      listingAlias: state.properties.currentProperty.listingAlias,
      isMultifamily: state.properties.currentProperty.isMultifamily,
      isFeed: state.properties.currentProperty.isFeed,
    }),
    shallowEqual,
  );

  const shouldShowLoading = isFromSOH && isConnectedListingLoading;
  const showIncompleteMessage = canResumeModularListingCreation || requiredFieldsErrors?.length > 0;

  const [displayScoreDetailsModal, setDisplayScoreDetailsModal] = useState(false);

  const { data: listingRecommendationsData, error: listingRecsError } = useGetListingRecommendations({
    listingAlias,
    isMultifamily,
  });

  useEffect(() => {
    if (listingRecommendationsData) {
      syncRecommendations({
        data: listingRecommendationsData,
        isMultifamily,
        dispatch,
      });
    }
  }, [listingRecommendationsData, isMultifamily]);

  if (
    !shouldShowLoading &&
    (listingRecsError ||
      !listingRecommendationsData ||
      !(
        listingRecommendationsData?.completeness?.rating in ListingScoreConstants ||
        listingRecommendationsData?.completeness?.rating in ZRAMListingScoreConstants
      ))
  ) {
    return null;
  }

  const { completeness, recommendations } = listingRecommendationsData || {};

  // @ts-expect-error
  const completenessScore: { score: number; rating: ListingScores } = showIncompleteMessage
    ? { score: 20, rating: 'incomplete' }
    : completeness;

  const totalIncompleteRecommendations =
    recommendations?.filter((item: RecommendationType) => !item.isComplete).length || 0;

  const getSubText = () => {
    if (isMultifamily) {
      if (showIncompleteMessage) {
        return 'Your listing won’t be published until required details are added. Complete the required fields to publish your listing.';
      }
      if (totalIncompleteRecommendations === 0) {
        return 'Your listing quality is outstanding and has all the details renters need!';
      }
      if (completeness?.leadImpact) {
        return `Adding missing details may help increase your leads by up to ${completeness.leadImpact}% and improve visibility in filtered renter searches.`;
      }
      return 'Add missing details to improve completeness to help prevent negative impacts to your leads and keep your listings visible in more filtered renter searches.';
    }

    if (showIncompleteMessage) {
      return `Complete ${stringHelper.pluralize(
        requiredFieldsErrors.length,
        'key section',
        'key sections',
      )} to publish your listing. `;
    } else if (totalIncompleteRecommendations === 0) {
      return isFromPremiumAnalyticsPage
        ? `Renters should have plenty of information to help decide if they're right for your rental. `
        : 'All listing recommendations complete. Great work! ';
    } else if (isFromPremiumAnalyticsPage) {
      const listingScoreDetails = ListingScoreConstants[completenessScore?.rating];
      return `${listingScoreDetails.premiumAnalyticsSubText} ${stringHelper.pluralizeWord(
        totalIncompleteRecommendations,
        'recommendation',
        'recommendations',
      )}. `;
    }

    return `Follow ${stringHelper.pluralize(
      totalIncompleteRecommendations,
      'listing recommendation',
      'listing recommendations',
    )} to help attract qualified renters. `;
  };

  const renderBody = () => (
    <>
      <Box
        data-testid="ListingCompletenessScore-Body"
        css={{
          display: 'flex',
          alignItems: isFromSOH ? 'flex-start' : 'center',
          flexDirection: isFromSOH ? 'column' : 'row',
        }}
      >
        {(isScreenDesktop || isFromSOH) && (
          <Box
            data-testid="LCS-RightPieChart"
            css={{
              alignSelf: isFromSOH ? 'flex-start' : 'center',
              marginRight: { base: 0, md: '400' },
              marginBottom: isFromSOH ? '200' : 0,
            }}
          >
            <ScorePieChart
              isRecsComplete={totalIncompleteRecommendations === 0}
              completeness={completenessScore}
              isLoading={shouldShowLoading}
              isMultifamily={isMultifamily}
            />
          </Box>
        )}

        <Box
          css={{
            flexGrow: 1,
            flexDirection: { base: 'column', xl: 'row' },
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Box
            css={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginRight: { base: 0, xl: 'tight' },
              marginBottom: previewListingCTA ? { base: '200', xl: 0 } : undefined,
              gap: 'tight',
            }}
          >
            <Box data-testid="LCS-HeaderAndSubText">
              <Box css={{ display: 'flex', alignItems: 'center' }}>
                <Heading
                  completenessScore={completenessScore}
                  showIncompleteMessage={showIncompleteMessage}
                  isLoading={shouldShowLoading}
                />

                {Boolean(!isFromSOH && isScreenMobile) && (
                  <ScorePieChart
                    customPieChartSize={30}
                    isRecsComplete={totalIncompleteRecommendations === 0}
                    completeness={completenessScore}
                    isMultifamily={isMultifamily}
                  />
                )}
              </Box>

              <LoadingText
                asChild={true}
                textStyle={isFromSOH ? 'body-sm' : 'body'}
                isLoading={shouldShowLoading}
                loadingWidth="10rem"
              >
                <Paragraph>
                  {getSubText()}

                  {Boolean(isFromSOH || Boolean(!isFromSOH && previewListingCTA)) && (
                    <TextButton
                      textStyle={isFromSOH ? 'body-sm' : 'body'}
                      onClick={() => setDisplayScoreDetailsModal(true)}
                    >
                      Learn more
                    </TextButton>
                  )}
                </Paragraph>
              </LoadingText>
            </Box>

            {Boolean(!isFromSOH && isScreenTablet) && (
              <ScorePieChart
                customPieChartSize={39}
                isRecsComplete={totalIncompleteRecommendations === 0}
                completeness={completenessScore}
                isMultifamily={isMultifamily}
              />
            )}
          </Box>

          {!isFromSOH && !previewListingCTA && (
            <Box css={{ alignSelf: { base: 'flex-start', xl: 'center' }, marginTop: { base: 'tightest', xl: 0 } }}>
              <TextButton onClick={() => setDisplayScoreDetailsModal(true)} css={{ width: 'max-content' }}>
                Learn more
              </TextButton>
            </Box>
          )}

          {previewListingCTA && previewListingCTA}
        </Box>
      </Box>
      {displayScoreDetailsModal && (
        <ScoreDetailsModal
          onClose={() => setDisplayScoreDetailsModal(false)}
          isRecsComplete={totalIncompleteRecommendations === 0}
          completeness={completenessScore}
          isFeed={isFeed}
          isMultifamily={isMultifamily}
        />
      )}
    </>
  );

  if (isFromSOH) {
    return renderBody();
  }

  return (
    <Card data-testid="ListingCompletenessScore" tone="neutral" elevated={isFromPremiumAnalyticsPage}>
      {renderBody()}
    </Card>
  );
};

export default ListingCompletenessScore;
